// Generated by GitHub Copilot
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { State, useComponentState } from '#core/state/State.ts'
import CompanyInvitationsApi from '#core/api/auth/CompanyInvitationsApi.ts'
import { ApiError } from '#core/api/Api.ts'
import { LanguageSelect } from '#components/composites/language-select.tsx'

const invitationsApi = new CompanyInvitationsApi()

type AcceptStep = 'form' | 'submitting' | 'success' | 'error'

/** State for the public invitation accept page. */
class AcceptInvitationState extends State<AcceptInvitationState> {
  firstName = ''
  lastName = ''
  password = ''
  step: AcceptStep = 'form'
  errorMessage: string | null = null

  reset(): void {
    this.reactTo.firstName = ''
    this.reactTo.lastName = ''
    this.reactTo.password = ''
    this.reactTo.step = 'form'
    this.reactTo.errorMessage = null
  }

  async accept(token: string): Promise<void> {
    this.reactTo.step = 'submitting'
    this.reactTo.errorMessage = null
    try {
      await invitationsApi.acceptInvitation(
        token,
        this.firstName || undefined,
        this.lastName || undefined,
        this.password || undefined,
      )
      this.reactTo.step = 'success'
    } catch (err) {
      if (err instanceof ApiError) {
        const status = err.response?.status
        if (status === 404) {
          this.reactTo.errorMessage = 'not_found'
        } else if (status === 409) {
          this.reactTo.errorMessage = 'already_used'
        } else if (status === 400) {
          this.reactTo.errorMessage = 'password_required'
        } else {
          this.reactTo.errorMessage = 'unknown'
        }
      } else {
        this.reactTo.errorMessage = 'unknown'
      }
      this.reactTo.step = 'error'
    }
  }
}

export const Route = createFileRoute('/_public/accept-invitation')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? '',
  }),
  component: AcceptInvitationPage,
})

function AcceptInvitationPage() {
  const { t } = useTranslation('settings')
  const { token } = Route.useSearch()
  const state = useComponentState(AcceptInvitationState)

  useEffect(() => {
    state.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (!token) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger">{t('acceptInvitation.noToken')}</div>
          </div>
        </div>
      </div>
    )
  }

  if (state.step === 'success') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h3 className="mb-3">{t('acceptInvitation.successTitle')}</h3>
            <p className="text-secondary">{t('acceptInvitation.successMessage')}</p>
            <a href="/login" className="btn btn-primary mt-3">{t('acceptInvitation.goToLogin')}</a>
          </div>
        </div>
      </div>
    )
  }

  if (state.step === 'error') {
    const messageKey = state.errorMessage === 'not_found'
      ? 'acceptInvitation.errorNotFound'
      : state.errorMessage === 'already_used'
        ? 'acceptInvitation.errorAlreadyUsed'
        : state.errorMessage === 'password_required'
          ? 'acceptInvitation.errorPasswordRequired'
          : 'acceptInvitation.errorUnknown'

    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger">{t(messageKey)}</div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => { state.reactTo.step = 'form' }}
            >
              {t('shared.actions.retry')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isSubmitting = state.step === 'submitting'

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          {/* The invitee lands here before any account exists, so this is their only way to
              read the form in their own language — short of letting the browser translate it. */}
          <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
            <h3 className="mb-0">{t('shared.fields.acceptInvitation')}</h3>
            <LanguageSelect style={{ maxWidth: '84px' }} />
          </div>
          <p className="text-secondary mb-4">{t('acceptInvitation.subtitle')}</p>

          <div className="d-flex flex-column gap-3">
            <div className="row g-3">
              <div className="col-6">
                <label className="form-label">{t('shared.fields.firstName')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={state.firstName}
                  onChange={e => { state.firstName = e.target.value }}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-6">
                <label className="form-label">{t('shared.fields.lastName')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={state.lastName}
                  onChange={e => { state.lastName = e.target.value }}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="form-label">{t('shared.fields.password')}</label>
              <input
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={state.password}
                onChange={e => { state.password = e.target.value }}
                disabled={isSubmitting}
              />
              <div className="form-text">{t('acceptInvitation.passwordHint')}</div>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              disabled={isSubmitting}
              onClick={() => void state.accept(token)}
            >
              {isSubmitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              {/* The label keeps its own element: a translator rewrites the text node inside it,
                  leaving the spinner a sibling React can still insert against. */}
              <span>{t('shared.fields.acceptInvitation')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
